import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { productUrl, crafterUrl } from '../utils/urls';
import { optimizeImageUrl } from '../utils/imageUrl';
import { calculateFinalPrice, calculateFullPrice, isLaunchModeActive, getTotalDiscountPercentage } from '../lib/feeCalculator';
import { sortProductsByAvailability, createAvailabilityComparator, getProductAvailability } from '../utils/productAvailability';
import { getChildCategoryName } from '../utils/categoryPath';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tricolore } from '../components/MadeInItaly';
import { ArrowRight, ChevronLeft, ChevronRight, FileText, ShoppingBag, Gift, Sparkles, Users, Star, Sprout } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default hero images fallback
const DEFAULT_HERO_IMAGES = [
  "https://images.pexels.com/photos/19208265/pexels-photo-19208265.jpeg?auto=compress&cs=tinysrgb&w=1920",
  "https://images.unsplash.com/photo-1771523350747-638a89667c8f?crop=entropy&cs=srgb&fm=jpg&w=1920",
  "https://images.pexels.com/photos/8665347/pexels-photo-8665347.jpeg?auto=compress&cs=tinysrgb&w=1920"
];

export default function HomePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [featuredCrafters, setFeaturedCrafters] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [heroImage, setHeroImage] = useState(null);
  const [productReviews, setProductReviews] = useState({});
  const heroLoadedRef = useRef(false);
  const PRODUCTS_PER_PAGE = 50;
  const carouselRef = useRef(null);

  useEffect(() => {
    document.title = 'Artigianato Made in Italy - Prodotti Unici Fatti a Mano | Rarifex';
    
    loadFeaturedCrafters();
    loadProducts(0);
    
    // Load hero image only once ever
    if (!heroLoadedRef.current) {
      heroLoadedRef.current = true;
      loadHeroImage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHeroImage = async () => {
    // Double check ref to prevent race conditions
    if (heroImage !== null) return;
    
    let images = DEFAULT_HERO_IMAGES;
    
    try {
      const response = await fetch(`${API_URL}/api/settings/hero-images`);
      if (response.ok) {
        const data = await response.json();
        if (data.images && data.images.length > 0) {
          images = data.images;
        }
      }
    } catch (error) {
      console.error('Error loading hero images:', error);
    }
    
    // Select random image
    const randomIndex = Math.floor(Math.random() * images.length);
    const selectedImage = images[randomIndex];
    
    // Pre-load the image before showing it
    const img = new Image();
    img.onload = () => {
      // Only set if not already set
      setHeroImage(prev => prev === null ? selectedImage : prev);
    };
    img.onerror = () => {
      setHeroImage(prev => prev === null ? DEFAULT_HERO_IMAGES[0] : prev);
    };
    img.src = selectedImage;
  };

  const loadFeaturedCrafters = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/crafters/featured?limit=20`);
      if (!resp.ok) return;
      const data = await resp.json();
      setFeaturedCrafters(data.crafters || []);
    } catch (err) {
      console.error('Failed to load featured crafters:', err);
    }
  };

  const loadProducts = async (pageNum) => {
    setLoading(true);
    const from = pageNum * PRODUCTS_PER_PAGE;

    try {
      // Ranking personalizzato SERVER-SIDE sull'intero catalogo: la navigazione recente
      // è il segnale dominante (real-time); popolarità/qualità/freschezza fanno da rifinitura.
      const sessionId = localStorage.getItem('rarifex_session_id');
      const optedOut = localStorage.getItem('rarifex_tracking_optout') === 'true';
      const params = new URLSearchParams();
      if (!optedOut && user?.id) params.set('user_id', user.id);
      if (!optedOut && sessionId) params.set('session_id', sessionId);
      params.set('limit', String(PRODUCTS_PER_PAGE));
      params.set('offset', String(from));

      const res = await fetch(`${API_URL}/api/products/ranked?${params.toString()}`);
      const payload = await res.json();
      const data = payload.products || [];

      // Carica le statistiche recensioni per i prodotti della pagina
      const productIds = data.map(p => p.id);
      if (productIds.length > 0) {
        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('product_id, rating')
          .in('product_id', productIds);

        if (reviewsData && reviewsData.length > 0) {
          const reviewMap = {};
          reviewsData.forEach(r => {
            if (!reviewMap[r.product_id]) {
              reviewMap[r.product_id] = { total: 0, count: 0 };
            }
            reviewMap[r.product_id].total += r.rating;
            reviewMap[r.product_id].count++;
          });
          Object.keys(reviewMap).forEach(productId => {
            reviewMap[productId].average = reviewMap[productId].total / reviewMap[productId].count;
          });
          setProductReviews(prev => ({ ...prev, ...reviewMap }));
        }
      }

      // L'ordine arriva già pronto dal backend: applichiamo solo lo spostamento
      // dei prodotti non disponibili in fondo (mantenendo l'ordine di ranking).
      const ordered = sortProductsByAvailability(data);

      if (pageNum === 0) {
        setProducts(ordered);
      } else {
        setProducts(prev => [...prev, ...ordered]);
      }
      setHasMore(from + data.length < (payload.total || 0));
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading ranked products:', err);
    }
    setLoading(false);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadProducts(page + 1);
    }
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const formatPrice = (price) => {
    // Apply Rarifex fee to get final price
    const finalPrice = calculateFinalPrice(price);
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(finalPrice);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[500px] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background solid color (always visible) */}
        <div 
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgb(0, 100, 100), rgb(0, 80, 80))' }}
        />
        
        {/* Background image with fade-in effect */}
        {heroImage && (
          <div 
            className="absolute inset-0 animate-fadeIn"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(0, 100, 100, 0.85), rgba(0, 80, 80, 0.88)), url('${heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'fadeIn 0.5s ease-in-out'
            }}
          />
        )}
        
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Made in Italy Badge */}
            <motion.div 
              className="flex justify-center mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Tricolore size="md" />
                <span className="text-white font-medium">100% Made in Italy</span>
              </div>
            </motion.div>
            
            <h1
              className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-4"
              style={{fontFamily: 'Inter, sans-serif'}}
              data-testid="hero-title"
            >
              L'Eccellenza Artigiana<br />
              <span className="text-[#DAA520]">Italiana</span>
            </h1>
            <p
              className="text-lg md:text-xl text-white/90 mb-3 max-w-3xl mx-auto"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Scopri creazioni uniche realizzate a mano dai migliori artigiani d'Italia
            </p>
            <p
              className="text-base text-[#DAA520] font-serif italic mb-8"
            >
              "Tradizione, Passione, Autenticità"
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/esplora">
                <Button
                  size="lg"
                  className="bg-[#DAA520] text-white hover:bg-[#DAA520]/90 rounded-sm px-8 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="browse-products-btn"
                >
                  Esplora Prodotti
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/regalo-personalizzato">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent text-white border-white hover:bg-white/10 rounded-sm px-8 py-3 font-medium transition-all duration-300"
                  data-testid="gift-request-btn-hero"
                >
                  <Gift className="mr-2 w-5 h-5" />
                  Crea regalo su misura
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#DAA520] rounded-sm transform rotate-45 blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#00CED1] rounded-sm transform rotate-45 blur-3xl"></div>
        </div>
      </section>

      {/* Gift Section - NEW */}
      <section className="py-8 bg-gradient-to-r from-rose-50 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            <div className="flex-shrink-0 text-center lg:text-left lg:max-w-md">
              <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-full text-sm font-medium mb-3">
                <Gift className="w-4 h-4" />
                Regali Personalizzati su Misura
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Non sai cosa regalare?
              </h2>
              <p className="text-slate-600 mb-4">
                Descrivi la tua idea e lascia che gli artigiani italiani creino qualcosa di unico. 
                <span className="font-medium text-slate-800"> Gratuito e senza impegno.</span>
              </p>
              <Link to="/regalo-personalizzato">
                <Button
                  size="default"
                  className="bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white rounded-sm px-6 py-2.5 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="gift-section-btn"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Crea il tuo regalo
                </Button>
              </Link>
            </div>
            
            {/* Responsive boxes container */}
            <div className="hidden lg:flex flex-1 justify-end items-end gap-3">
              {/* Box 1 - Compleanno - visible lg+ */}
              <div className="bg-white rounded-xl shadow-lg p-3 transform rotate-3 hover:rotate-0 transition-transform">
                <div className="w-20 h-20 xl:w-24 xl:h-24 bg-gradient-to-br from-rose-100 to-amber-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl xl:text-3xl">🎁</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center">Compleanno</p>
              </div>
              
              {/* Box 2 - Anniversario - visible lg+ */}
              <div className="bg-white rounded-xl shadow-lg p-3 transform -rotate-2 hover:rotate-0 transition-transform -mb-4">
                <div className="w-20 h-20 xl:w-24 xl:h-24 bg-gradient-to-br from-amber-100 to-rose-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl xl:text-3xl">💍</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center">Anniversario</p>
              </div>
              
              {/* Box 3 - Laurea - visible xl+ */}
              <div className="hidden xl:block bg-white rounded-xl shadow-lg p-3 transform rotate-2 hover:rotate-0 transition-transform">
                <div className="w-20 h-20 xl:w-24 xl:h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl xl:text-3xl">🎓</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center">Laurea</p>
              </div>
              
              {/* Box 4 - Matrimonio - visible 2xl+ */}
              <div className="hidden 2xl:block bg-white rounded-xl shadow-lg p-3 transform -rotate-3 hover:rotate-0 transition-transform -mb-2">
                <div className="w-24 h-24 bg-gradient-to-br from-sky-100 to-indigo-100 rounded-lg flex items-center justify-center">
                  <span className="text-3xl">💒</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center">Matrimonio</p>
              </div>
              
              {/* Box 5 - Natale - visible 2xl+ */}
              <div className="hidden 2xl:block bg-white rounded-xl shadow-lg p-3 transform rotate-1 hover:rotate-0 transition-transform">
                <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-3xl">🎄</span>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 text-center">Natale</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Crafters Carousel Section */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Inter, sans-serif'}}>
                {t('featured_crafters')}
              </h2>
              <p className="text-slate-600 text-sm mt-1">Scopri i migliori artigiani italiani</p>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/crafters" 
                className="text-sm text-[#008080] hover:text-[#006666] font-medium hidden sm:flex items-center gap-1 transition-colors"
                data-testid="view-all-crafters-link"
              >
                Vedi tutti
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollCarousel('left')}
                className="rounded-full h-10 w-10"
                data-testid="carousel-left"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollCarousel('right')}
                className="rounded-full h-10 w-10"
                data-testid="carousel-right"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Carousel */}
          <div 
            ref={carouselRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {featuredCrafters.map((crafter, index) => (
              <Link
                key={crafter.id}
                to={crafterUrl(crafter)}
                className="flex-shrink-0 w-[280px]"
                style={{ scrollSnapAlign: 'start' }}
                data-testid={`crafter-card-${index}`}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                  <div
                    className="h-32 bg-gradient-to-br from-[#008080] to-[#00CED1] relative"
                    style={{
                      backgroundImage: crafter.cover_image ? `url(${optimizeImageUrl(crafter.cover_image, 500)})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    {crafter.tier === 'pro' && (
                      <Badge className="absolute top-2 right-2 bg-[#DAA520] text-white">
                        Pro
                      </Badge>
                    )}
                    {crafter.is_newcomer && (
                      <Badge
                        className="absolute top-2 left-2 bg-emerald-500 text-white border-0 flex items-center gap-1 shadow-md"
                        data-testid={`newcomer-badge-${index}`}
                      >
                        <Sprout className="w-3 h-3" />
                        Nuovo Artigiano
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-full bg-slate-200 border-2 border-white shadow-md -mt-8"
                        style={{
                          backgroundImage: crafter.avatar_url ? `url(${optimizeImageUrl(crafter.avatar_url, 96)})` : undefined,
                          backgroundSize: 'cover',
                        }}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base truncate">{crafter.full_name || 'Artigiano'}</h3>
                        {crafter.city && (
                          <p className="text-xs text-slate-500 truncate">{crafter.city}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mt-3">
                      {crafter.bio || 'Artigiano specializzato in creazioni uniche.'}
                    </p>
                  </div>
                </Card>
              </Link>
            ))}
            {featuredCrafters.length === 0 && (
              <div className="flex-shrink-0 w-full text-center py-8 text-slate-500">
                <p>Nessun artigiano disponibile al momento.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Request Section - Compact */}
      <section className="py-8 bg-gradient-to-r from-[#008080] to-[#006666]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Header */}
            <div className="flex-shrink-0 text-center md:text-left md:w-64">
              <div className="inline-flex items-center gap-2 mb-2">
                <Tricolore size="sm" />
                <span className="text-white/70 text-xs">100% Made in Italy</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">
                Hai un progetto in mente?
              </h2>
              <p className="text-white/70 text-sm mt-1">
                Creazioni uniche su misura
              </p>
            </div>
            
            {/* Cards */}
            <div className="flex-1 grid md:grid-cols-2 gap-4">
              {/* For Clients */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#DAA520] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm">Sei un Cliente?</h3>
                  <p className="text-white/70 text-xs truncate">Pubblica la tua richiesta</p>
                </div>
                <Link to="/requests?create=true">
                  <Button
                    size="sm"
                    className="bg-[#DAA520] text-white hover:bg-[#DAA520]/90 rounded-sm text-xs px-3"
                    data-testid="post-request-btn"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Pubblica
                  </Button>
                </Link>
              </div>
              
              {/* For Crafters */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm">Sei un Artigiano?</h3>
                  <p className="text-white/70 text-xs truncate">Scopri le richieste aperte</p>
                </div>
                <Link to="/requests">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-transparent text-white border-white/50 hover:bg-white/10 rounded-sm text-xs px-3"
                    data-testid="browse-requests-btn"
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Esplora
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Inter, sans-serif'}}>
              Creazioni Artigianali
            </h2>
            <p className="text-slate-600 text-sm mt-1">Esplora le ultime creazioni dei nostri artigiani</p>
          </div>

          {products.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="products-grid">
                {products.map((product, index) => {
                  const mainImgIndex = product.customization_options?.main_image_index || 0;
                  const mainImage = optimizeImageUrl(product.images?.[mainImgIndex] || product.images?.[0], 500);
                  
                  return (
                    <Link
                      key={product.id}
                      to={productUrl(product)}
                      className="block"
                      data-testid={`product-card-${index}`}
                    >
                      <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full border-slate-100 flex flex-col">
                        <div
                          className="aspect-square bg-white relative flex-shrink-0"
                          style={{
                            backgroundImage: mainImage ? `url(${mainImage})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          {!mainImage && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <ShoppingBag className="w-10 h-10 text-slate-300" />
                            </div>
                          )}
                          {product.category && (
                            <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700 text-xs">
                              {getChildCategoryName(product.customization_options?.categoryPath) ||
                               product.customization_options?.categoryPath?.sub ||
                               product.category}
                            </Badge>
                          )}
                          {/* Availability + customization badges - bottom left */}
                          {(() => {
                            const { isAvailable, isOnDemand } = getProductAvailability(product);
                            const hasCustomizations = product.customization_options?.customizations?.length > 0;
                            if (!isAvailable) {
                              return (
                                <Badge className="absolute bottom-2 left-2 bg-red-500 text-white text-xs">
                                  Esaurito
                                </Badge>
                              );
                            }
                            if (hasCustomizations) {
                              return (
                                <Badge className="absolute bottom-2 left-2 bg-[#DAA520] text-white text-xs">
                                  Personalizzabile
                                </Badge>
                              );
                            }
                            if (isOnDemand) {
                              return (
                                <Badge className="absolute bottom-2 left-2 bg-[#008080] text-white text-xs">
                                  Su commissione
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="p-3 flex flex-col flex-1">
                          <h3 className="font-medium text-sm line-clamp-1 mb-1">{product.title}</h3>
                          
                          {/* Crafter info */}
                          {product.profiles && (
                            <div className="flex items-center gap-1.5 mb-2">
                              <div
                                className="w-5 h-5 rounded-full bg-slate-200"
                                style={{
                                  backgroundImage: product.profiles.avatar_url ? `url(${optimizeImageUrl(product.profiles.avatar_url, 48)})` : undefined,
                                  backgroundSize: 'cover',
                                }}
                              ></div>
                              <span className="text-xs text-slate-500 truncate">
                                {product.profiles.full_name || 'Artigiano'}
                              </span>
                            </div>
                          )}
                          
                          {/* Review preview - min-height ensures consistent card height */}
                          <div className="min-h-[20px] mb-2">
                            {productReviews[product.id] && productReviews[product.id].count > 0 && (
                              <div className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-[#DAA520] fill-[#DAA520]" />
                                <span className="text-xs font-medium text-slate-700">
                                  {productReviews[product.id].average.toFixed(1)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  ({productReviews[product.id].count})
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between mt-auto">
                            {(() => {
                              const crafterDiscount = product.customization_options?.crafterDiscount || 0;
                              const launchModeActive = isLaunchModeActive();
                              const hasDiscount = crafterDiscount > 0 || launchModeActive;
                              const originalPrice = calculateFullPrice(product.base_price);
                              const finalPrice = calculateFinalPrice(product.base_price, crafterDiscount);
                              const totalDiscountPercent = getTotalDiscountPercentage(crafterDiscount);
                              
                              if (hasDiscount && originalPrice !== finalPrice) {
                                return (
                                  <>
                                    <div className="flex flex-col">
                                      <span className="text-xs text-slate-400 line-through">
                                        €{originalPrice.toFixed(2)}
                                      </span>
                                      <span className="text-base font-bold text-[#008080]">
                                        €{finalPrice.toFixed(2)}
                                      </span>
                                    </div>
                                    <Badge className={`${crafterDiscount > 0 && launchModeActive ? 'bg-gradient-to-r from-orange-500 to-green-500' : crafterDiscount > 0 ? 'bg-orange-500' : 'bg-green-500'} text-white text-[10px] px-1.5 py-0.5`}>
                                      -{totalDiscountPercent}%
                                    </Badge>
                                  </>
                                );
                              }
                              return (
                                <span className="text-base font-bold text-[#008080]">
                                  €{finalPrice.toFixed(2)}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-10">
                  <Button
                    onClick={loadMore}
                    disabled={loading}
                    size="lg"
                    variant="outline"
                    className="px-12 border-[#008080] text-[#008080] hover:bg-[#008080] hover:text-white"
                    data-testid="load-more-btn"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
                        Caricamento...
                      </>
                    ) : (
                      <>
                        Mostra altre creazioni
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008080] mx-auto"></div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-slate-200" data-testid="no-products-message">
              <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nessun prodotto disponibile al momento.</p>
              <p className="text-sm text-slate-400 mt-2">I nostri artigiani stanno preparando nuove creazioni!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#008080] text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{fontFamily: 'Inter, sans-serif'}}
          >
            Sei un Artigiano?
          </h2>
          <p className="text-lg mb-8 text-white/90">
            Unisciti a Rarifex e fai scoprire le tue creazioni uniche. Inizia con il piano gratuito.
          </p>
          <Link to="/signup">
            <Button
              size="lg"
              className="bg-[#DAA520] text-white hover:bg-[#DAA520]/90 rounded-sm px-8 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="join-as-crafter-btn"
            >
              Inizia Ora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
